import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FiSearch, FiShoppingCart, FiUser, FiMenu, FiX,
  FiChevronDown, FiLogOut, FiPackage, FiSettings, FiPhone, FiMessageCircle, FiClock
} from 'react-icons/fi';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import { getImageUrl, formatPrice } from '../../utils/helpers';
import API from '../../api/axios';

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [userOpen, setUserOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const { itemCount } = useCart();
  const { user, logout, isAdmin } = useAuth();
  const { settings } = useSettings();
  const userRef = useRef(null);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    API.get('/categories').then(({ data }) => setCategories(data.slice(0, 9))).catch(() => {});
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (userRef.current && !userRef.current.contains(e.target)) setUserOpen(false);
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowSuggestions(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchSuggestions = useCallback((q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.length < 2) { setSuggestions([]); setShowSuggestions(false); return; }
    debounceRef.current = setTimeout(async () => {
      setSuggestLoading(true);
      try {
        const { data } = await API.get(`/products/autocomplete?q=${encodeURIComponent(q)}`);
        setSuggestions(data);
        setShowSuggestions(data.length > 0);
      } catch {
        setSuggestions([]);
      } finally {
        setSuggestLoading(false);
      }
    }, 280);
  }, []);

  const handleQueryChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    fetchSuggestions(val);
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setShowSuggestions(false);
      setSearchQuery('');
      setSuggestions([]);
    }
  };

  const pickSuggestion = (p) => {
    navigate(`/product/${p._id}`);
    setSearchQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    setSearchOpen(false);
  };

  const SearchBox = ({ mobile = false }) => (
    <div ref={mobile ? null : searchRef} className={`relative ${mobile ? 'w-full' : 'flex-1 max-w-2xl'}`}>
      <form onSubmit={handleSearch} className="flex w-full rounded-xl overflow-visible shadow-sm border border-gray-200 focus-within:border-primary-400 focus-within:shadow-md transition-all bg-white">
        <input
          type="text"
          placeholder="Search medical equipment, devices..."
          value={searchQuery}
          onChange={handleQueryChange}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          className="flex-1 px-4 py-2.5 text-sm focus:outline-none bg-transparent min-w-0 rounded-l-xl"
          autoComplete="off"
        />
        {searchQuery && (
          <button type="button" onClick={() => { setSearchQuery(''); setSuggestions([]); setShowSuggestions(false); }} className="px-2 text-gray-400 hover:text-gray-600">
            <FiX size={14} />
          </button>
        )}
        <button type="submit" className="bg-primary-600 hover:bg-primary-700 text-white px-5 transition-colors flex items-center gap-1.5 text-sm font-medium flex-shrink-0 rounded-r-xl">
          <FiSearch size={16} /> {!mobile && 'Search'}
        </button>
      </form>

      {/* Suggestions dropdown */}
      {showSuggestions && (
        <div className="absolute top-full left-0 right-0 mt-1.5 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-[200]">
          {suggestLoading ? (
            <div className="flex items-center gap-2 px-4 py-3 text-sm text-gray-400">
              <span className="w-4 h-4 border-2 border-gray-200 border-t-primary-500 rounded-full animate-spin" />
              Searching...
            </div>
          ) : (
            <>
              <p className="px-4 pt-2.5 pb-1 text-xs font-bold text-gray-400 uppercase tracking-wider">Suggestions</p>
              {suggestions.map((p) => (
                <button
                  key={p._id}
                  type="button"
                  onClick={() => pickSuggestion(p)}
                  className="flex items-center gap-3 w-full px-4 py-2.5 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                    {p.images?.[0] ? (
                      <img src={getImageUrl(p.images[0])} alt={p.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-300">
                        <FiPackage size={14} />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{p.name}</p>
                    <p className="text-xs text-primary-600 font-semibold">{formatPrice(p.price)}</p>
                  </div>
                </button>
              ))}
              <button
                type="button"
                onClick={handleSearch}
                className="flex items-center gap-2 w-full px-4 py-3 border-t border-gray-100 text-sm text-primary-600 font-semibold hover:bg-primary-50 transition-colors"
              >
                <FiSearch size={14} /> Search for "{searchQuery}"
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Announcement bar */}
      {settings.showAnnouncement && settings.announcement && (
        <div className="bg-accent-500 text-white text-center text-sm py-2 px-4 font-medium">
          {settings.announcement}
        </div>
      )}

      {/* Top bar */}
      <div className="bg-primary-800 text-white text-xs hidden md:block">
        <div className="container-custom flex justify-between items-center py-2">
          <div className="flex items-center gap-4 text-primary-200">
            <span className="flex items-center gap-1.5">
              <FiPhone size={11} />
              {settings.phone}
            </span>
            <span>{settings.email}</span>
          </div>
          <div className="flex items-center gap-3 text-primary-200">
            {settings.whatsapp && (
              <a href={`https://wa.me/${settings.whatsapp.replace(/\D/g,'')}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-white transition-colors">
                <FiMessageCircle size={12} /> WhatsApp
              </a>
            )}
            <Link to="/account/orders" className="flex items-center gap-1 hover:text-white transition-colors">
              <FiClock size={11} /> Track Order
            </Link>
            <Link to="/contact" className="hover:text-white transition-colors">Contact Us</Link>
            {!user && <Link to="/login" className="hover:text-white transition-colors">Login</Link>}
          </div>
        </div>
      </div>

      {/* Main navbar */}
      <nav className={`bg-white sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'shadow-lg' : 'shadow-sm border-b border-gray-100'}`}>
        <div className="container-custom">
          <div className="flex items-center justify-between h-16 gap-4">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 flex-shrink-0">
              {settings.logo ? (
                <img src={getImageUrl(settings.logo)} alt={settings.siteName} className="h-10 w-auto object-contain" />
              ) : (
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center shadow-sm">
                    <span className="text-white font-bold text-lg">H</span>
                  </div>
                  <div className="hidden sm:block">
                    <p className="text-lg font-extrabold text-primary-700 leading-none">{settings.siteName || 'Hospitex'}</p>
                    <p className="text-[10px] text-gray-400 leading-none font-medium tracking-wide">.com.pk</p>
                  </div>
                </div>
              )}
            </Link>

            {/* Search bar (desktop) */}
            <div className="hidden md:flex flex-1 max-w-2xl">
              <SearchBox />
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1">
              <button onClick={() => setSearchOpen(!searchOpen)} className="md:hidden p-2 text-gray-600 hover:text-primary-500 hover:bg-gray-50 rounded-xl transition-all">
                <FiSearch size={21} />
              </button>

              <Link to="/cart" className="relative p-2 text-gray-600 hover:text-primary-500 hover:bg-gray-50 rounded-xl transition-all">
                <FiShoppingCart size={21} />
                {itemCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-gradient-to-br from-red-400 to-red-600 text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-bold shadow-sm">
                    {itemCount > 9 ? '9+' : itemCount}
                  </span>
                )}
              </Link>

              <div className="relative" ref={userRef}>
                <button onClick={() => setUserOpen(!userOpen)} className="flex items-center gap-1 p-2 text-gray-600 hover:text-primary-500 hover:bg-gray-50 rounded-xl transition-all">
                  {user ? (
                    <div className="w-7 h-7 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-primary-700 font-bold text-sm">{user.name?.[0]}</span>
                    </div>
                  ) : (
                    <FiUser size={21} />
                  )}
                  <FiChevronDown size={13} className={`hidden sm:block transition-transform duration-200 ${userOpen ? 'rotate-180' : ''}`} />
                </button>
                {userOpen && (
                  <div className="absolute right-0 mt-2 w-52 bg-white rounded-2xl shadow-2xl border border-gray-100 py-2 z-50">
                    {user ? (
                      <>
                        <div className="px-4 py-3 border-b border-gray-100">
                          <div className="flex items-center gap-2.5">
                            <div className="w-9 h-9 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center">
                              <span className="text-white font-bold">{user.name?.[0]}</span>
                            </div>
                            <div>
                              <p className="font-semibold text-sm text-gray-800">{user.name}</p>
                              <p className="text-xs text-gray-400 truncate">{user.email}</p>
                            </div>
                          </div>
                        </div>
                        <Link to="/account" onClick={() => setUserOpen(false)} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                          <FiUser size={14} className="text-gray-400" /> My Profile
                        </Link>
                        <Link to="/account/orders" onClick={() => setUserOpen(false)} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                          <FiPackage size={14} className="text-gray-400" /> My Orders
                        </Link>
                        {isAdmin && (
                          <Link to="/admin" onClick={() => setUserOpen(false)} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-primary-600 hover:bg-primary-50 font-medium transition-colors">
                            <FiSettings size={14} /> Admin Panel
                          </Link>
                        )}
                        <div className="border-t border-gray-100 mt-1 pt-1">
                          <button onClick={() => { logout(); setUserOpen(false); }} className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors">
                            <FiLogOut size={14} /> Sign Out
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="p-3 space-y-2">
                        <Link to="/login" onClick={() => setUserOpen(false)} className="block btn-primary text-center py-2 text-sm">Sign In</Link>
                        <Link to="/register" onClick={() => setUserOpen(false)} className="block btn-secondary text-center py-2 text-sm">Create Account</Link>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden p-2 text-gray-600 hover:bg-gray-50 rounded-xl transition-all">
                {menuOpen ? <FiX size={22} /> : <FiMenu size={22} />}
              </button>
            </div>
          </div>

          {/* Category nav strip */}
          <div className="hidden md:flex items-center gap-1 py-2 border-t border-gray-50 overflow-x-auto scrollbar-hide">
            <Link to="/shop" className="flex-shrink-0 text-xs font-bold text-primary-600 bg-primary-50 hover:bg-primary-100 px-3 py-1.5 rounded-full transition-colors">
              All Products
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat._id}
                to={`/shop?category=${cat._id}`}
                className="flex-shrink-0 text-xs text-gray-600 hover:text-primary-600 hover:bg-gray-50 px-3 py-1.5 rounded-full transition-colors whitespace-nowrap"
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>

        {/* Mobile search */}
        {searchOpen && (
          <div className="md:hidden px-4 pb-3 border-t border-gray-100 pt-3" ref={searchRef}>
            <SearchBox mobile />
          </div>
        )}

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden border-t border-gray-100 bg-white">
            <div className="px-4 py-4 grid grid-cols-2 gap-2">
              <Link to="/shop" onClick={() => setMenuOpen(false)} className="col-span-2 text-sm font-bold text-primary-600 bg-primary-50 rounded-xl px-4 py-2.5">All Products</Link>
              {categories.map((cat) => (
                <Link key={cat._id} to={`/shop?category=${cat._id}`} onClick={() => setMenuOpen(false)} className="text-sm text-gray-600 hover:text-primary-600 bg-gray-50 rounded-xl px-3 py-2 truncate">
                  {cat.name}
                </Link>
              ))}
            </div>
            <div className="px-4 pb-4 border-t border-gray-100 pt-3 flex gap-2">
              <Link to="/contact" onClick={() => setMenuOpen(false)} className="flex-1 text-center text-sm text-gray-600 bg-gray-50 rounded-xl px-3 py-2">Contact</Link>
              <Link to="/account/orders" onClick={() => setMenuOpen(false)} className="flex-1 text-center text-sm text-gray-600 bg-gray-50 rounded-xl px-3 py-2">Track Order</Link>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navbar;
